import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Form,
  FormSubmitHandler,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  AddableTitleExtensionFeature,
  TitleExtensionEquipmentResponse,
  useGetAddableTitleExtensionFeaturesQuery,
  useResetTitleExtensionEquipmentMutation,
  useSaveTitleExtensionEquipmentMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useCountry, useTenant} from '@dms/shared';

import {ApiError} from 'shared/web';

import {
  TitleExtensionEquipmentFormValues,
  titleExtensionEquipmentDraftSchema,
} from '../schemas/titleExtensionEquipmentDraftSchema';
import {
  hydrateTitleExtensionEquipmentDraft,
  normalizeTitleExtensionEquipmentDraft,
  serializeTitleExtensionEquipmentDraft,
  TitleExtensionEquipmentDraftItem,
} from '../utils/hydrateTitleExtensionEquipmentDraft';
import {TitleExtensionEquipmentFields} from './TitleExtensionEquipmentFields';

interface TitleExtensionEquipmentFormProps {
  equipment: TitleExtensionEquipmentResponse;
  isEditable: boolean;
  onReload: () => Promise<TitleExtensionEquipmentResponse>;
  onResetRecoveryStateChange: (isPending: boolean) => void;
}

export function TitleExtensionEquipmentForm(props: TitleExtensionEquipmentFormProps) {
  const defaultValues = hydrateTitleExtensionEquipmentDraft(props.equipment);
  const [savedValues, setSavedValues] = useState(defaultValues);
  const [activeLanguage, setActiveLanguage] = useState(props.equipment.languages[0]);
  const [searchValue, setSearchValue] = useState('');
  const [removedItems, setRemovedItems] = useState<TitleExtensionEquipmentDraftItem[]>([]);
  const [isUsingCountryDefault, setIsUsingCountryDefault] = useState(props.equipment.isDefault);
  const [isReloadingDefaults, setIsReloadingDefaults] = useState(false);
  const [hasResetReloadError, setHasResetReloadError] = useState(false);
  const addableFeaturesQuery = useGetAddableTitleExtensionFeaturesQuery(
    props.isEditable ? undefined : skipToken
  );
  const [saveEquipment, saveEquipmentState] = useSaveTitleExtensionEquipmentMutation();
  const [resetEquipment, resetEquipmentState] = useResetTitleExtensionEquipmentMutation();
  const {getCountryName} = useCountry();
  const {tenant} = useTenant();
  const countryName = getCountryName(props.equipment.country) ?? props.equipment.country;
  const tenantDefaultLanguage = tenant?.defaultLocale.split('-')[0];
  const defaultLanguage = props.equipment.languages.find(
    (language) => language === tenantDefaultLanguage
  );

  const handleSubmit: FormSubmitHandler<TitleExtensionEquipmentFormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const normalizedValues = normalizeTitleExtensionEquipmentDraft(values);

    await saveEquipment({
      titleExtensionEquipmentRequestBody: serializeTitleExtensionEquipmentDraft(values),
    })
      .unwrap()
      .then(() => {
        reset(normalizedValues);
        setSavedValues(normalizedValues);
        setRemovedItems([]);
        setIsUsingCountryDefault(false);
        setHasResetReloadError(false);
        props.onResetRecoveryStateChange(false);
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<TitleExtensionEquipmentFormValues>
      defaultValues={defaultValues}
      experimentalZodSchema={titleExtensionEquipmentDraftSchema}
      onSubmit={handleSubmit}
      onInvalidSubmit={(errors) => {
        setSearchValue('');

        if (!Array.isArray(errors.items)) {
          return;
        }

        for (const itemError of errors.items) {
          if (!Array.isArray(itemError?.shortNames)) {
            continue;
          }

          const invalidLanguageIndex = itemError?.shortNames?.findIndex(
            (shortNameError) => shortNameError?.shortName
          );
          const invalidLanguage = props.equipment.languages.at(invalidLanguageIndex);

          if (invalidLanguageIndex >= 0 && isNotNil(invalidLanguage)) {
            setActiveLanguage(invalidLanguage);
            return;
          }
        }
      }}
      isDisabled={!props.isEditable}
      shouldWatchForUnsavedChanges={props.isEditable}
    >
      {(control, formApi) => {
        const adoptCountryDefaults = (equipment: TitleExtensionEquipmentResponse) => {
          const countryDefaults = hydrateTitleExtensionEquipmentDraft(equipment);
          formApi.reset(countryDefaults);
          setSavedValues(countryDefaults);
          setRemovedItems([]);
          setIsUsingCountryDefault(true);
          setHasResetReloadError(false);
          props.onResetRecoveryStateChange(false);
          showNotification.success(i18n.t('page.titleExtensionEquipment.notifications.reset'));
        };

        const handleResetReloadError = (error: ApiError) => {
          setHasResetReloadError(true);
          props.onResetRecoveryStateChange(true);
          handleApiError(error);
        };

        const handleRetryResetReload = () => {
          setIsReloadingDefaults(true);

          return props
            .onReload()
            .then(adoptCountryDefaults)
            .catch(handleResetReloadError)
            .finally(() => setIsReloadingDefaults(false));
        };

        const handleDiscard = () => {
          formApi.reset(savedValues);
          setRemovedItems([]);
        };

        const handleReset = () => {
          openConfirmDialog({
            text: i18n.t('page.titleExtensionEquipment.labels.resetConfirmation'),
            'data-testid': 'title-extension-equipment-reset-confirmation',
            onConfirm: () => {
              setIsReloadingDefaults(true);

              return resetEquipment()
                .unwrap()
                .then(() =>
                  props.onReload().then(adoptCountryDefaults).catch(handleResetReloadError)
                )
                .catch((error: ApiError) => handleApiError(error))
                .finally(() => setIsReloadingDefaults(false));
            },
          });
        };

        const resolvedActiveLanguage = activeLanguage ?? props.equipment.languages[0];
        const addableFeatures: AddableTitleExtensionFeature[] =
          addableFeaturesQuery.data?.items ?? [];

        return (
          <Box width="100%" maxWidth={375}>
            <VStack spacing={6}>
              <Show when={isUsingCountryDefault}>
                <Alert
                  variant="info"
                  message={i18n.t('page.titleExtensionEquipment.labels.countryDefaultNotice', {
                    country: countryName,
                  })}
                />
              </Show>

              <Show when={hasResetReloadError}>
                <Alert
                  variant="error"
                  layout="detailed"
                  message={i18n.t('general.labels.somethingWentWrong')}
                  actions={[
                    {
                      variant: 'secondary',
                      title: i18n.t('general.actions.retry'),
                      onClick: handleRetryResetReload,
                      isDisabled: isReloadingDefaults || saveEquipmentState.isLoading,
                    },
                  ]}
                />
              </Show>

              <TitleExtensionEquipmentFields
                control={control}
                languages={props.equipment.languages}
                activeLanguage={resolvedActiveLanguage}
                defaultLanguage={defaultLanguage}
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                onLanguageChange={(language) => {
                  const selectedLanguage = props.equipment.languages.find(
                    (candidate) => candidate === language
                  );
                  if (selectedLanguage) {
                    setActiveLanguage(selectedLanguage);
                  }
                }}
                isEditable={props.isEditable}
                isInteractionDisabled={isReloadingDefaults || saveEquipmentState.isLoading}
                addableFeatures={addableFeatures}
                isAddableFeaturesLoading={addableFeaturesQuery.isLoading}
                isAddableFeaturesError={addableFeaturesQuery.isError}
                onRetryAddableFeatures={() => {
                  void addableFeaturesQuery.refetch();
                }}
                removedItems={removedItems}
                setRemovedItems={setRemovedItems}
                onReset={handleReset}
                isResetting={resetEquipmentState.isLoading}
                onSeparatorPresetChange={(separator) => {
                  formApi.setValue('separator', separator, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
              />

              <Show when={props.isEditable}>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      variant: 'secondary',
                      title: i18n.t('general.actions.discardChanges'),
                      onClick: handleDiscard,
                      isDisabled: isReloadingDefaults || saveEquipmentState.isLoading,
                    },
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                      isDisabled: isReloadingDefaults || saveEquipmentState.isLoading,
                    },
                  ]}
                />
              </Show>
            </VStack>
          </Box>
        );
      }}
    </Form>
  );
}
